import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { companyInfo } from "@/data/mock";
import { Menu, X, Phone, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: "Inicio" },
    { to: "/acerca", label: "Acerca de" },
    { to: "/servicios", label: "Servicios" },
    { to: "/contacto", label: "Contacto" }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top bar */}
      <div className="hidden md:block bg-onix-dark border-b border-cyan-900/30">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center">
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
              <Phone size={14} /> {companyInfo.phone}
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
              <Mail size={14} /> {companyInfo.email}
            </a>
          </div>
          <div className="text-sm text-cyan-400 font-medium tracking-wide">
            {companyInfo.tagline}
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-onix-dark/95 backdrop-blur-xl shadow-lg shadow-cyan-500/5 border-b border-cyan-900/20"
          : "bg-onix-dark/80 backdrop-blur-md"
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={companyInfo.logo}
              alt="Grupo Onix Logo"
              className="h-12 w-12 object-contain group-hover:scale-105 transition-transform"
            />
            <div>
              <span className="text-xl font-orbitron font-bold text-white tracking-wider">
                GRUPO <span className="text-cyan-400">ONIX</span>
              </span>
            </div>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative px-5 py-2.5 rounded-lg text-sm font-medium tracking-wide transition-all duration-300 ${
                  isActive(link.to)
                    ? "text-cyan-400"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                {link.label}
                {isActive(link.to) && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute bottom-0 left-2 right-2 h-0.5 bg-cyan-400 rounded-full"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
            <Link
              to="/contacto"
              className="ml-4 px-6 py-2.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm font-medium hover:bg-cyan-500/20 hover:border-cyan-400/50 transition-all duration-300"
            >
              Cotizar Ahora
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-slate-300 hover:text-cyan-400 transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden bg-onix-dark/98 backdrop-blur-xl border-t border-cyan-900/20"
            >
              <div className="px-6 py-4 flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive(link.to)
                        ? "bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-400"
                        : "text-slate-300 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/contacto"
                  className="mt-2 px-4 py-3 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm font-medium text-center"
                >
                  Cotizar Ahora
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};
